import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { Proximamente } from '@/features/comun/Proximamente';
import { protegida } from '@/features/comun/SinAcceso';
import { PendientesPage } from '@/features/comun/PendientesPage';
import { AgendaPage, type BusquedaAgenda } from '@/features/agenda/AgendaPage';
import { CajaPage, type BusquedaCaja } from '@/features/caja/CajaPage';
import { ImportacionPage } from '@/features/importacion/ImportacionPage';
import { ReportesPage } from '@/features/reportes/ReportesPage';
import { AprobacionesPage } from '@/features/aprobaciones/AprobacionesPage';
import { PromocionesPage } from '@/features/promociones/PromocionesPage';
import { ClientesPage } from '@/features/clientes/ClientesPage';
import { AuditoriaPage } from '@/features/configuracion/AuditoriaPage';
import { ServiciosPage } from '@/features/configuracion/ServiciosPage';
import { ComprasPage } from '@/features/inventario/ComprasPage';
import { InventarioPage } from '@/features/inventario/InventarioPage';
import { ProveedoresPage } from '@/features/inventario/ProveedoresPage';
import { AsistenciaPage } from '@/features/nomina/AsistenciaPage';
import { BovedaPage } from '@/features/boveda/BovedaPage';
import { NominaPage } from '@/features/nomina/NominaPage';
import { HorariosPage } from '@/features/personal/HorariosPage';
import { VacacionesPage } from '@/features/personal/VacacionesPage';
import { ConfiguracionPage } from '@/features/configuracion/ConfiguracionPage';
import { MercadoPagoPage } from '@/features/configuracion/MercadoPagoPage';
import { SucursalPage } from '@/features/configuracion/SucursalPage';
import { UsuariosPage } from '@/features/configuracion/UsuariosPage';
import { InicioPage } from '@/features/inicio/InicioPage';

const raiz = createRootRoute({ component: Outlet });

const login = createRoute({ getParentRoute: () => raiz, path: '/login', component: LoginPage });

/** Rutas que requieren sesión: comparten barra superior y buscador. */
const app = createRoute({ getParentRoute: () => raiz, id: 'app', component: AppShell });

const ruta = <P extends string>(path: P, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => app, path, component });

const configuracion = createRoute({
  getParentRoute: () => app,
  path: '/configuracion',
  component: ConfiguracionPage,
  validateSearch: (search: Record<string, unknown>): { clave?: string } =>
    typeof search.clave === 'string' ? { clave: search.clave } : {},
});

const texto = (v: unknown) => (typeof v === 'string' && v ? v : undefined);

const agenda = createRoute({
  getParentRoute: () => app,
  path: '/agenda',
  component: AgendaPage,
  validateSearch: (s: Record<string, unknown>): BusquedaAgenda => ({
    dia: texto(s.dia),
    cita: texto(s.cita),
    pendientes: s.pendientes === true || s.pendientes === '1' || s.pendientes === 1 ? true : undefined,
    vista: s.vista === 'semana' ? 'semana' : undefined,
  }),
});

const clientes = createRoute({
  getParentRoute: () => app,
  path: '/clientes',
  component: ClientesPage,
  validateSearch: (s: Record<string, unknown>): { id?: string } => ({ id: texto(s.id) }),
});

export const arbolRutas = raiz.addChildren([
  login,
  app.addChildren([
    ruta('/', InicioPage),
    agenda,
    createRoute({
      getParentRoute: () => app,
      path: '/caja',
      component: protegida(['caja.operar', 'ventas.cobrar'], CajaPage),
      // Los avisos de cobro con tarjeta y de reembolso enlazan a `/caja?venta=…&reembolso=…`.
      validateSearch: (s: Record<string, unknown>): BusquedaCaja => ({ venta: texto(s.venta), reembolso: texto(s.reembolso) }),
    }),
    ruta('/caja/boveda', protegida(['boveda.ver'], BovedaPage)),
    // La sección Gastos pasó a ser Bóveda: los enlaces viejos siguen funcionando.
    createRoute({
      getParentRoute: () => app,
      path: '/caja/gastos',
      beforeLoad: () => {
        throw redirect({ to: '/caja/boveda' });
      },
    }),
    ruta('/caja/promociones', protegida(['ventas.cobrar'], PromocionesPage)),
    ruta('/caja/importar', protegida(['importacion.usar'], ImportacionPage)),
    clientes,
    createRoute({
      getParentRoute: () => app,
      path: '/inventario',
      component: InventarioPage,
      validateSearch: (s: Record<string, unknown>): { id?: string } => ({ id: texto(s.id) }),
    }),
    ruta('/inventario/compras', protegida(['inventario.ver'], ComprasPage)),
    ruta('/inventario/proveedores', protegida(['inventario.ver'], ProveedoresPage)),
    ruta('/personal', HorariosPage),
    ruta('/personal/vacaciones', VacacionesPage),
    ruta('/personal/asistencia', AsistenciaPage),
    ruta('/personal/nomina', protegida(['nomina.ver'], NominaPage)),
    ruta('/reportes', protegida(['reportes.dueno', 'reportes.recepcion', 'reportes.propios'], ReportesPage)),
    ruta('/sincronizacion', PendientesPage),
    ruta('/aprobaciones', protegida(['aprobaciones.resolver'], AprobacionesPage)),
    configuracion,
    ruta('/configuracion/servicios', ServiciosPage),
    ruta('/configuracion/usuarios', protegida(['usuarios.gestionar'], UsuariosPage)),
    ruta('/configuracion/sucursal', SucursalPage),
    ruta('/configuracion/mercadopago', protegida(['config.mercadopago'], MercadoPagoPage)),
    ruta('/configuracion/auditoria', protegida(['auditoria.ver'], AuditoriaPage)),
  ]),
]);

export const router = createRouter({
  routeTree: arbolRutas,
  defaultPreload: 'intent',
  defaultNotFoundComponent: () => <Proximamente titulo="Página no encontrada" />,
});

// Nota: no se registra el tipo del router (`Register`). Con rutas hijas de un layout sin path,
// la inferencia de search params de esta versión colapsa; cada ruta valida su search en runtime.
